import React from 'react'
import {
  Globe2,
  Plane,
  Ship,
  PackageSearch,
  ShieldCheck,
  Clock,
  CheckCircle,
  MoveRight,
  FileText,
  ClipboardCheck,
  ArrowRight,
  Headphones,
  Truck
} from 'lucide-react'

import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'

import processImage from '../assets/images/data/international-shipping.png'
import MoreServicesSection from './MoreServicesSection'
import CTA from './CTA'

const services = [
  {
    number: '01',
    icon: Plane,
    title: 'International Air Freight',
    description:
      'Air cargo solutions for businesses requiring efficient international transportation of shipments.'
  },
  {
    number: '02',
    icon: Ship,
    title: 'International Sea Freight',
    description:
      'Ocean freight solutions for bulk, commercial and containerized cargo movement.'
  },
  {
    number: '03',
    icon: FileText,
    title: 'Export & Import Support',
    description:
      'Documentation coordination and logistics support for international trade requirements.'
  },
  {
    number: '04',
    icon: ShieldCheck,
    title: 'Customs Coordination',
    description:
      'Assistance with customs-related documentation and coordination during cross-border shipping.'
  },
  {
    number: '05',
    icon: PackageSearch,
    title: 'Shipment Tracking',
    description:
      'Shipment milestone visibility to help businesses monitor international consignments.'
  },
  {
    number: '06',
    icon: Truck,
    title: 'Door-to-Door Logistics',
    description:
      'Coordinated transportation from origin pickup to final delivery, subject to service availability.'
  }
]

const benefits = [
  {
    icon: Globe2,
    title: 'Global Connectivity',
    description:
      'International logistics coordination to support shipments across global trade lanes.'
  },
  {
    icon: ShieldCheck,
    title: 'Documentation Support',
    description:
      'Assistance with shipment documentation and customs-related coordination.'
  },
  {
    icon: Clock,
    title: 'Transit Coordination',
    description:
      'Coordinated shipment movement with attention to transit schedules and milestones.'
  },
  {
    icon: Headphones,
    title: 'Dedicated Assistance',
    description:
      'Customer-focused support for international shipping and logistics requirements.'
  }
]

const processSteps = [
  {
    number: '01',
    icon: ClipboardCheck,
    title: 'Share Shipment Details',
    description:
      'Provide shipment information, origin, destination, cargo details and delivery requirements.'
  },
  {
    number: '02',
    icon: FileText,
    title: 'Documentation & Planning',
    description:
      'Coordinate shipping documentation and plan the suitable international transportation solution.'
  },
  {
    number: '03',
    icon: Plane,
    title: 'International Movement',
    description:
      'Your shipment moves through the selected air or sea freight logistics network.'
  },
  {
    number: '04',
    icon: CheckCircle,
    title: 'Destination Delivery',
    description:
      'Coordinate destination handling and final delivery according to the selected service.'
  }
]

const industries = [
  {
    title: 'Manufacturing & Industrial'
  },
  {
    title: 'Retail & E-Commerce'
  },
  {
    title: 'Import & Export Businesses'
  },
  {
    title: 'Wholesale & Distribution'
  }
]

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
}

const sectionAnimation = {
  initial: 'hidden',
  whileInView: 'visible',
  viewport: { once: true, amount: 0.15 },
  variants: fadeUp,
  transition: { duration: 0.6 }
}

export default function InternationalShippingPage () {
  return (
    <>
      <Helmet>
        <title>International Shipping Services | S INFINITE EXPRESS</title>

        <meta
          name='description'
          content='Explore international shipping solutions by S INFINITE EXPRESS, including air freight, sea freight, export and import documentation support, customs coordination and global logistics.'
        />

        <meta
          name='keywords'
          content='International Shipping India, Global Logistics, International Air Freight, International Sea Freight, Export Import Logistics, Customs Clearance Support, Cross Border Shipping, International Cargo, S Infinite Express'
        />

        <meta name='author' content='S Infinite Express' />
        <meta name='robots' content='index, follow' />

        <link
          rel='canonical'
          href='https://sinfiniteexpress.com/services/international'
        />

        <meta
          property='og:title'
          content='International Shipping Services | S INFINITE EXPRESS'
        />

        <meta
          property='og:description'
          content='Simplify cross-border shipping with international air freight, sea freight, documentation support and logistics coordination from S INFINITE EXPRESS.'
        />

        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/services/international'
        />

        <meta property='og:type' content='website' />
      </Helmet>

      <main className='overflow-hidden bg-white'>
        {/* HERO SECTION */}
        <section className='relative bg-gradient-to-br from-white via-blue-50/40 to-white overflow-hidden'>
          <div className='max-w-7xl mx-auto px-5 sm:px-8 lg:px-6 py-8 md:py-12'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center'>
              {/* LEFT CONTENT */}
              <motion.div {...sectionAnimation} className='space-y-6'>
                <h1 className='text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-[#211f57] leading-tight'>
                  International
                  <span className='text-[#d32c30]'> Shipping Solutions</span>
                </h1>

                <div className='w-24 h-1.5 bg-[#d32c30] rounded-full' />

                <p className='text-gray-600 text-base sm:text-lg leading-relaxed'>
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>{' '}
                  helps businesses move cargo beyond borders through coordinated
                  international air freight, sea freight, shipment documentation
                  and destination handling solutions.
                </p>

                <p className='text-gray-600 leading-relaxed'>
                  From shipment planning and export-import support to
                  international transportation coordination, we help simplify
                  your global logistics requirements.
                </p>

                {/* ACTION BUTTONS */}
                <div className='flex flex-col sm:flex-row gap-4 pt-3'>
                  <a
                    href='/get-a-quote'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#d32c30] text-white font-semibold rounded-xl shadow-lg hover:bg-[#b82629] hover:-translate-y-1 transition-all duration-300'
                  >
                    Get Shipping Quote
                    <MoveRight className='w-5 h-5' />
                  </a>

                  <a
                    href='/track-shipment'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 border-2 border-[#211f57] text-[#211f57] font-semibold rounded-xl hover:bg-[#211f57] hover:text-white transition-all duration-300'
                  >
                    Track Shipment
                    <ArrowRight className='w-5 h-5' />
                  </a>
                </div>

                {/* TRUST POINTS */}
                <div className='flex flex-wrap gap-x-6 gap-y-3 pt-5 text-sm font-medium text-gray-600'>
                  {[
                    'International Air & Sea Freight',
                    'Documentation Support',
                    'Global Shipment Coordination'
                  ].map((item, index) => (
                    <span key={index} className='flex items-center gap-2'>
                      <CheckCircle className='w-4 h-4 text-green-600 shrink-0' />
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* RIGHT IMAGE */}
              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                <div className='absolute -top-6 -right-5 w-32 h-32 bg-[#fff44f]/60 rounded-full blur-3xl' />

                <div className='absolute -bottom-6 -left-5 w-32 h-32 bg-blue-200/70 rounded-full blur-3xl' />

                <div className='relative p-2 bg-white rounded-3xl shadow-2xl border border-gray-100'>
                  <img
                    src={processImage}
                    alt='International air freight and sea freight logistics'
                    className='w-full h-[200px] sm:h-[300px] lg:h-[420px] object-cover rounded-2xl'
                    fetchPriority='high'
                    decoding='async'
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* INTRODUCTION SECTION */}
        <section className='relative overflow-hidden bg-gradient-to-br from-yellow-50 via-blue-50/40 to-yellow-100/60 py-8 sm:py-10 lg:py-12'>
          {/* BACKGROUND DECORATIONS */}
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />

          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12'>
            <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16'>
              {/* LEFT CONTENT */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className='space-y-6'
              >
                <span className='text-[#d32c30] font-extrabold uppercase tracking-[2px] text-xs'>
                  International Logistics Solutions
                </span>

                <h2 className='text-2xl font-bold leading-tight text-[#211f57] sm:text-2xl lg:text-3xl'>
                  Global Shipping
                  <span className='text-[#d32c30]'> Without Complexity</span>
                </h2>

                <p className='text-base leading-relaxed text-gray-600 sm:text-lg'>
                  At{' '}
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>
                  , we provide international shipping solutions designed to
                  support businesses involved in cross-border movement of goods.
                  From air freight and sea freight to documentation
                  coordination, we help simplify the logistics process.
                </p>

                <p className='leading-relaxed text-gray-600'>
                  Our approach focuses on shipment planning, transportation
                  coordination, visibility and destination handling to help
                  businesses manage their international logistics requirements.
                </p>

                {/* KEY FEATURES */}
                <div className='grid grid-cols-1 gap-x-5 gap-y-4 pt-3 sm:grid-cols-2'>
                  {[
                    'International Air Freight',
                    'International Sea Freight',
                    'Export & Import Support',
                    'Coordinated Global Logistics'
                  ].map((item, index) => (
                    <div key={index} className='flex items-center gap-3'>
                      <CheckCircle
                        size={20}
                        className='shrink-0 text-green-600'
                      />

                      <span className='text-sm font-semibold text-[#211f57]'>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                <a
                  href='/get-a-quote'
                  className='inline-flex items-center gap-3 font-bold text-[#d32c30] hover:gap-5 transition-all duration-300'
                >
                  Discuss Your Shipping Requirements
                  <ArrowRight className='w-5 h-5' />
                </a>
              </motion.div>

              {/* RIGHT INFORMATION CARD */}
              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                <div className='absolute -right-5 -top-6 h-32 w-32 rounded-full bg-[#fff44f]/50 blur-3xl' />

                <div className='absolute -bottom-6 -left-5 h-32 w-32 rounded-full bg-blue-200/70 blur-3xl' />

                <div className='relative overflow-hidden rounded-3xl bg-[#211f57] p-3 shadow-2xl sm:p-4 lg:p-5'>
                  <div className='absolute -right-16 -top-16 h-48 w-48 rounded-full border-[25px] border-white/5' />

                  <div className='absolute -bottom-20 -right-10 h-56 w-56 rounded-full border-[25px] border-white/5' />

                  <div className='relative z-10'>
                    <div className='mb-3 flex items-center gap-4'>
                      <div>
                        <span className='text-xs font-bold uppercase tracking-[2px] text-[#fff44f]'>
                          Our Capabilities
                        </span>
                      </div>
                    </div>

                    <p className='mb-4 text-sm leading-7 text-white/70 sm:text-base'>
                      Integrated international shipping solutions designed to
                      simplify cross-border freight movement and support global
                      business requirements.
                    </p>

                    <div className='space-y-2'>
                      {[
                        {
                          title: 'International Air Freight',
                          desc: 'Air cargo transportation for international shipments.'
                        },
                        {
                          title: 'International Sea Freight',
                          desc: 'Ocean freight coordination for commercial cargo.'
                        },
                        {
                          title: 'Documentation Support',
                          desc: 'Export-import documentation coordination.'
                        },
                        {
                          title: 'Shipment Visibility',
                          desc: 'Shipment milestone and delivery coordination.'
                        }
                      ].map((item, index) => {
                        return (
                          <div
                            key={index}
                            className='flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-all duration-300 hover:bg-white/10'
                          >
                            <div>
                              <h4 className='text-sm font-bold text-white sm:text-base'>
                                {item.title}
                              </h4>

                              <p className='mt-1 text-xs leading-5 text-white/55 sm:text-sm'>
                                {item.desc}
                              </p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* INTERNATIONAL SHIPPING SERVICES */}
        <section className='py-5 bg-[#f7f8fc]'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='max-w-4xl mx-auto text-center mb-5'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                What We Offer
              </span>

              <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211f57]'>
                Our International Shipping Services
              </h2>

              <p className='text-gray-500 leading-7 mt-2'>
                Flexible freight and logistics support to help businesses manage
                international shipments and cross-border transportation.
              </p>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
              {services.map((service, index) => {
                const Icon = service.icon

                return (
                  <motion.div
                    key={service.number}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    className='group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#d32c30]/30 hover:shadow-xl transition-all duration-300'
                  >
                    <div className='flex justify-between items-center'>
                      <div className='w-14 h-14 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] flex items-center justify-center text-[#ffff] group-hover:text-white transition-colors'>
                        <Icon size={26} />
                      </div>

                      <span className='text-3xl font-black text-gray-100 group-hover:text-[#d32c30]/20 transition-colors'>
                        {service.number}
                      </span>
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-3'>
                      {service.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-2'>
                      {service.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* WHY CHOOSE US */}
        <section className='relative overflow-hidden bg-gradient-to-br from-white via-blue-50/40 to-yellow-50 py-4 sm:py-5 lg:py-6'>
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />

          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative max-w-7xl mx-auto px-5 sm:px-5 lg:px-5'>
            <div className='text-center max-w-5xl mx-auto mb-5'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                The S Infinite Advantage
              </span>

              <h2 className='text-2xl sm:text-3xl lg:text-3xl font-extrabold text-[#211f57]'>
                Why Choose S INFINITE EXPRESS?
              </h2>

              <p className='text-gray-500 leading-7'>
                A coordinated approach to international shipping with
                transportation options, documentation support and shipment
                visibility.
              </p>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'>
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    className='group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#d32c30]/30 hover:shadow-xl transition-all duration-300'
                  >
                    <div className='w-14 h-14 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] flex items-center justify-center text-[#ffff] group-hover:text-white transition-colors'>
                      <Icon size={26} />
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-4'>
                      {benefit.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-2'>
                      {benefit.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* INTERNATIONAL SHIPPING PROCESS */}
        <section className='py-5 lg:py-5 bg-white'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='text-center max-w-5xl mx-auto mb-4'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                How It Works
              </span>

              <h2 className='text-2xl sm:text-3xl font-extrabold text-[#211f57]'>
                Our International Shipping Process
              </h2>

              <p className='text-gray-500 leading-7'>
                From shipment planning to destination delivery, every stage is
                coordinated to support a smooth international shipping journey.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-8 relative'>
              {processSteps.map((step, index) => {
                const Icon = step.icon

                return (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className='relative text-center group'
                  >
                    <div className='relative w-16 h-16 mx-auto rounded-2xl bg-[#211f57] text-[#ffff] flex items-center justify-center shadow-lg group-hover:bg-[#d32c30] group-hover:text-white transition-all duration-300'>
                      <Icon size={25} />
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-6'>
                      {step.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-3'>
                      {step.description}
                    </p>

                    {index !== processSteps.length - 1 && (
                      <div className='hidden lg:block absolute top-8 left-[calc(50%+45px)] w-[calc(100%-50px)] border-t-2 border-dashed border-[#d32c30]/30' />
                    )}
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* INDUSTRIES WE SERVE */}
        <section className='relative overflow-hidden bg-gradient-to-br from-yellow-50 via-blue-50/40 to-yellow-100/60 py-4 sm:py-5 lg:py-6'>
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />

          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12'>
            <div className='grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-12 items-center'>
              {/* LEFT CONTENT */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <span className='text-[#d32c30] font-extrabold uppercase tracking-[0.2em] text-xs'>
                  Industry Expertise
                </span>

                <h2 className='text-2xl sm:text-3xl lg:text-3xl font-extrabold text-[#211f57] mt-4 mb-6'>
                  Empowering Global Business
                </h2>

                <div className='w-20 h-1.5 bg-[#d32c30] rounded-full mb-6' />

                <p className='text-gray-600 leading-relaxed mb-7'>
                  International shipping requirements vary across industries.
                  Our logistics solutions support businesses with different
                  cargo, transportation and cross-border movement needs.
                </p>

                <a
                  href='/industry-expertise'
                  className='inline-flex items-center gap-3 px-6 py-3 bg-[#211f57] text-white rounded-xl font-bold text-sm hover:bg-[#d32c30] transition-all duration-300'
                >
                  Explore Industries
                  <MoveRight className='w-4 h-4' />
                </a>
              </motion.div>

              {/* INDUSTRY CARDS */}
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-5'>
                {industries.map((industry, index) => {
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.08 }}
                      className='flex items-center gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300'
                    >
                      <h3 className='font-extrabold text-[#211f57]'>
                        {industry.title}
                      </h3>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className='relative bg-[#211f57] py-5 overflow-hidden'>
          <div className='absolute right-0 top-0 w-96 h-96 rounded-full border-[50px] border-white/5 translate-x-1/3 -translate-y-1/3' />

          <div className='relative max-w-5xl mx-auto px-6 text-center'>
            <h2 className='text-2xl sm:text-3xl lg:text-3xl font-extrabold text-white mt-2 leading-tight'>
              Ready to Ship
              <span className='block text-[#FFF44F]'>
                Beyond International Borders?
              </span>
            </h2>

            <p className='text-white/70 max-w-4xl mx-auto mt-4 leading-8'>
              Connect with S Infinite Express for international freight,
              documentation and coordinated global logistics solutions.
            </p>
          </div>
        </section>

        {/* MORE SERVICES */}
        <MoreServicesSection />

        {/* COMMON CTA */}
        <CTA />
      </main>
    </>
  )
}
